import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useVerifyTwoFactorMutation } from 'http-client/hooks/authHooks';
import { TwoFactorDto, ApiResponse, LoginResponseDto } from 'http-client/types/auth.types';
import { toast } from 'react-toastify';

// Meta title

const TwoFactorVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const { mutate: verifyTwoFactor, isPending, error } = useVerifyTwoFactorMutation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailOrPhone: email,
      emailOtp: '',
      phoneOtp: '',
    } as TwoFactorDto,
    validationSchema: Yup.object({
      emailOrPhone: Yup.string().email('Invalid email address').required('Email is required'),
      emailOtp: Yup.string()
        .matches(/^\d{6}$/, 'Email OTP must be a 6-digit number')
        .required('Email OTP is required'),
      phoneOtp: Yup.string()
        .matches(/^\d{6}$/, 'Phone OTP must be a 6-digit number')
        .required('Phone OTP is required'),
    }),
    onSubmit: (values) => {
      verifyTwoFactor(values, {
        onSuccess: (response: ApiResponse<LoginResponseDto>) => {
          if (!response?.data?.requiresTwoFactor) {
            navigate('/dashboard');
          }
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message ?? 'Verification failed.');
        },
      });
    },
  });

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Two-Factor Verification</h5>
                        <p>Enter the OTPs sent to your email and phone.</p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {error && (
                        <Alert color="danger">
                          {error?.response?.data?.message || 'Verification failed'}
                        </Alert>
                      )}

                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="Enter email"
                          type="email"
                          value={validation.values.emailOrPhone}
                          readOnly
                        />
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Email OTP</Label>
                        <Input
                          name="emailOtp"
                          className="form-control"
                          placeholder="Enter 6-digit email OTP"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.emailOtp}
                          invalid={validation.touched.emailOtp && !!validation.errors.emailOtp}
                        />
                        {validation.touched.emailOtp && validation.errors.emailOtp ? (
                          <FormFeedback type="invalid">{validation.errors.emailOtp}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Phone OTP</Label>
                        <Input
                          name="phoneOtp"
                          className="form-control"
                          placeholder="Enter 6-digit phone OTP"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phoneOtp}
                          invalid={validation.touched.phoneOtp && !!validation.errors.phoneOtp}
                        />
                        {validation.touched.phoneOtp && validation.errors.phoneOtp ? (
                          <FormFeedback type="invalid">{validation.errors.phoneOtp}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                          disabled={isPending}
                        >
                          {isPending ? 'Verifying...' : 'Verify OTPs'}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <a href="/login" className="text-muted">
                          <i className="mdi mdi-arrow-left me-1" />
                          Back to Login
                        </a>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  © {new Date().getFullYear()} Pharmacy Management. Crafted with{' '}
                  <i className="mdi mdi-heart text-danger" /> by Your Team
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TwoFactorVerification;
